"use client";

import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/app/_module/components/ui/card";
import { Trophy, Flame } from "lucide-react";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";

/**
 * The agent's own goal, on their dashboard.
 *
 * Renders NOTHING unless a goal is actually configured for them. An agency that
 * doesn't use goals should not get an empty widget taking up the slot, and an agent
 * with no target shouldn't be shown a 0/0 bar suggesting they're failing at something.
 *
 * Labels/units mirror GOAL_METRICS on the lead-assignment side so the number an agent
 * sees here reads the same as the one their manager set. Kept as a local copy rather
 * than imported: pulling from a lead-assignment component would drag that whole module
 * (and its queries) into the dashboard bundle.
 */
const METRIC_LABELS: Record<string, { label: string; unit: string; prefix?: string }> = {
  lead_count: { label: "Leads", unit: "" },
  dials: { label: "Dials", unit: "" },
  talk_time: { label: "Talk Time", unit: "m" },
  completed_calls: { label: "Completed Calls", unit: "" },
  contacts: { label: "Contacts", unit: "" },
  appointments: { label: "Appointments", unit: "" },
  premium: { label: "Premium Sold", unit: "", prefix: "$" },
  policies_submitted: { label: "Policies Submitted", unit: "" },
};

const PERIOD_LABEL: Record<string, string> = {
  daily: "today",
  weekly: "this week",
  monthly: "this month",
};

interface GoalData {
  goalType: string;
  goalValue: number;
  goalDuration: string;
  goalCurrent: number;
  agencyName: string | null;
}

/**
 * Radial progress ring.
 *
 * A flat bar in a narrow sidebar column reads as an afterthought and leaves the card
 * looking empty; a ring gives the number somewhere to live and is legible at a glance
 * from across a desk — which is the actual use (an agent checking "am I on track?").
 *
 * strokeDasharray/offset is the standard circumference trick. `rotate(-90)` starts the
 * arc at 12 o'clock instead of 3 o'clock, and `strokeLinecap="round"` keeps a 2% sliver
 * from rendering as an invisible hairline.
 */
function GoalRing({
  pct,
  hit,
  children,
}: {
  pct: number;
  hit: boolean;
  children: React.ReactNode;
}) {
  const r = 52;
  const circumference = 2 * Math.PI * r;
  // Clamped for the ARC only — an agent at 140% still reads 140% in the label below.
  const shown = Math.max(0, Math.min(pct, 100));
  const offset = circumference - (shown / 100) * circumference;

  return (
    // 40 (160px), up from 32 (128px). At 128px a premium figure like "$4,444" rendered
    // wider than the ring's inner circle and visibly overhung the stroke on both sides —
    // and premium is the metric most likely to be long ("$120,000" is 8 characters).
    // The inner content is additionally width-capped and font-scaled below.
    <div className="relative h-40 w-40 shrink-0">
      <svg className="h-full w-full -rotate-90" viewBox="0 0 120 120">
        <circle
          cx="60"
          cy="60"
          r={r}
          fill="none"
          strokeWidth="10"
          className="stroke-gray-100"
        />
        <circle
          cx="60"
          cy="60"
          r={r}
          fill="none"
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className={`transition-[stroke-dashoffset] duration-700 ease-out ${
            hit ? "stroke-emerald-500" : "stroke-primary"
          }`}
        />
      </svg>
      {/* Inset by the stroke so content is bounded by the ring's INNER circle, not the
          outer box — without this the text was free to run out over the stroke.
          `px-2` keeps a little breathing room off the curve at the widest point. */}
      <div className="absolute inset-[14px] flex flex-col items-center justify-center px-2 text-center">
        {children}
      </div>
    </div>
  );
}

export default function AgentGoalCard() {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const { data: goal, isLoading } = useQuery<GoalData | null>({
    // Browser timezone so "today" starts at the agent's midnight, not UTC's — the
    // Agency Dashboard already measures whole local days for the same agent.
    queryKey: ["myGoal", tz],
    queryFn: async () => {
      const res = await api.get<{ data: GoalData | null }>({
        url: getApiUrl(`/dashboard/my-goal?tz=${encodeURIComponent(tz)}`),
        auth: true,
      });
      return (res as any)?.data ?? null;
    },
    staleTime: 60_000,
  });

  // No goal set, or still loading → render nothing at all (not a skeleton). This slot
  // is optional by design, so a placeholder would be more confusing than absence.
  if (isLoading || !goal || !goal.goalValue) return null;

  const meta = METRIC_LABELS[goal.goalType] || { label: goal.goalType, unit: "" };
  const fmt = (n: number) =>
    `${meta.prefix || ""}${Number(n || 0).toLocaleString("en-US")}${meta.unit || ""}`;

  const pct = (goal.goalCurrent / goal.goalValue) * 100;
  const hit = goal.goalCurrent >= goal.goalValue;
  const close = !hit && pct >= 75;
  const remaining = Math.max(0, goal.goalValue - goal.goalCurrent);

  return (
    // Height: a bounded `min-h`, never `h-full`.
    //
    // `h-full` made this stretch to the height of the whole main content column beside
    // it — a tiny card in a sea of white. But sizing purely to content left it looking
    // orphaned above the leftover gap at the bottom of this (shorter) sidebar column.
    // A min-height gives it presence and matches the visual rhythm of the min-h wrappers
    // its siblings use, while still being bounded.
    //
    // The residual gap under it is the grid column being shorter than the main content
    // column — it predates this card and can't be closed from inside it.
    <Card
      className={`shadow-sm border min-h-[320px] flex flex-col ${
        hit ? "border-emerald-300 bg-emerald-50/40" : "border-gray-200"
      }`}
    >
      {/* flex-1 + justify-between spreads the three blocks (header / ring / footer) over
          the min-height instead of bunching them at the top with dead space beneath. */}
      <CardContent className="p-5 flex-1 flex flex-col justify-between gap-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            {/* "My Quota", not "My Goal" — the number is ASSIGNED by the agency, not
                chosen by the agent, and "quota" is the word the owners actually use.
                The agency line says who set it, which also disambiguates for an agent
                who belongs to more than one. */}
            <p className="font-semibold text-gray-900 text-sm leading-tight">My Quota</p>
            <p className="text-xs text-gray-500 truncate">
              {meta.label} · {PERIOD_LABEL[goal.goalDuration] || goal.goalDuration}
            </p>
          </div>
          {hit && (
            <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-emerald-700 bg-emerald-100 px-2 py-1 rounded">
              <Trophy className="h-3 w-3" /> Hit
            </span>
          )}
          {close && (
            <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-100 px-2 py-1 rounded">
              <Flame className="h-3 w-3" /> Almost
            </span>
          )}
        </div>

        <div className="flex flex-col items-center gap-3">
          <GoalRing pct={pct} hit={hit}>
            {/* Font scales with the string length. A fixed size only ever fits the value
                it was eyeballed against: "12" is fine at 2xl, "$4,444" already overhung
                the ring, and a real monthly premium like "$120,000" would be far worse.
                Measured on the formatted string, so the $ and separators count. */}
            <span
              className={`font-bold leading-none ${
                fmt(goal.goalCurrent).length > 7
                  ? "text-lg"
                  : fmt(goal.goalCurrent).length > 5
                  ? "text-xl"
                  : "text-2xl"
              } ${hit ? "text-emerald-600" : "text-gray-900"}`}
            >
              {fmt(goal.goalCurrent)}
            </span>
            <span className="text-[11px] text-gray-500 mt-1 leading-tight">
              of {fmt(goal.goalValue)}
            </span>
          </GoalRing>

          <div className="text-center">
            <p
              className={`text-sm font-semibold ${
                hit ? "text-emerald-700" : "text-gray-900"
              }`}
            >
              {/* True percentage, uncapped — the RING clamps at 100%, this doesn't. */}
              {pct.toFixed(0)}% complete
            </p>
            <p className="text-xs text-gray-500">
              {hit
                ? `${fmt(goal.goalCurrent - goal.goalValue)} over target`
                : `${fmt(remaining)} to go ${PERIOD_LABEL[goal.goalDuration] || ""}`}
            </p>
          </div>
        </div>

        {goal.agencyName && (
          <p className="text-[11px] text-gray-400 text-center border-t pt-3 truncate">
            Set by {goal.agencyName}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
